/**
 * Leyes y normas y Convenios en la dirección v2 (DESIGN.md §11.6, maquetas m5, m6 y m7). Funciones puras sobre los
 * datos tal como vienen de la base (consultas de solo lectura del 19-10, anotadas en la nota del lead):
 * - convenios: país en `legislacion[0].pais` sin tildes y con mayúsculas dispares; `clase` vacía en 36 de 77 (se deduce
 *   del título); un solo protocolo «suscrito, no vigente»;
 * - leyes: título oficial en mayúsculas, materias en `materia` separadas por coma (con repetidos), artículos del texto
 *   marcados con una línea «Artículo N».
 * Se muestran en forma legible sin tocar el dato.
 */
import type { Document } from './supabase';

type DocNorma = Pick<Document, 'titulo' | 'tipo'> & {
  numero?: string | null;
  fecha_publicacion?: string | null;
  materia?: string | null;
  legislacion?: unknown;
};

const plegar = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
const primero = (doc: DocNorma): Record<string, unknown> => {
  const leg = doc.legislacion;
  return Array.isArray(leg) && leg[0] && typeof leg[0] === 'object' ? (leg[0] as Record<string, unknown>) : {};
};
const texto = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

// ─── Convenios ──────────────────────────────────────────────────────────────────────────────────────────────

/** Nombres con tilde de los países que la base guarda sin ella. */
const PAIS_LEGIBLE: Record<string, string> = {
  espana: 'España', belgica: 'Bélgica', canada: 'Canadá', japon: 'Japón', mexico: 'México', peru: 'Perú',
  panama: 'Panamá', 'paises bajos': 'Países Bajos', 'republica checa': 'República Checa', sudafrica: 'Sudáfrica',
};
const MENORES = new Set(['de', 'del', 'la', 'las', 'los', 'y', 'e']);

/** «estados unidos» → «Estados Unidos»; «Espana» → «España». Vacío si el convenio no trae país. */
export function paisConvenio(doc: DocNorma): string {
  const crudo = texto(primero(doc).pais);
  if (!crudo) return '';
  const clave = plegar(crudo);
  if (PAIS_LEGIBLE[clave]) return PAIS_LEGIBLE[clave];
  return crudo.toLowerCase().split(/\s+/).map((p, i) => (i > 0 && MENORES.has(p) ? p : p.charAt(0).toUpperCase() + p.slice(1))).join(' ');
}

/** Clave para agrupar y ordenar por país sin que «alemania» y «Alemania» queden en grupos distintos. */
export const clavePais = (doc: DocNorma) => plegar(paisConvenio(doc));
/** Clave de orden: los convenios sin país van al final. */
const ordenPais = (doc: DocNorma) => clavePais(doc) || '\uffff';

export type ClaseConvenio = 'convenio' | 'mli' | 'protocolo' | 'transporte' | 'intercambio' | 'multilateral';

/** Clase del documento: la de `legislacion.clase` y, si viene vacía, la que dice el título. */
export function claseConvenio(doc: DocNorma): ClaseConvenio {
  const c = plegar(texto(primero(doc).clase));
  if (c === 'mli-sintetizado') return 'mli';
  if (c === 'mli') return 'multilateral';
  if (c === 'protocolo' || c === 'transporte' || c === 'intercambio') return c;
  const t = plegar(doc.titulo || '');
  if (/protocolo/.test(t)) return 'protocolo';
  if (/transporte/.test(t)) return 'transporte';
  if (/intercambio de informacion/.test(t)) return 'intercambio';
  if (/sintetizado|\bmli\b/.test(t)) return 'mli';
  return 'convenio';
}

/** Pestaña del control segmentado de Convenios (m7) a la que pertenece cada clase. */
export const PESTANA_CONVENIO: Record<ClaseConvenio, 'convenios' | 'mli' | 'protocolos' | 'transporte' | 'intercambio'> = {
  convenio: 'convenios', mli: 'mli', multilateral: 'mli', protocolo: 'protocolos', transporte: 'transporte', intercambio: 'intercambio',
};
export const PESTANAS_CONVENIO = [
  { valor: 'convenios', texto: 'Convenios' },
  { valor: 'mli', texto: 'Textos con MLI' },
  { valor: 'protocolos', texto: 'Protocolos' },
  { valor: 'transporte', texto: 'Transporte' },
  { valor: 'intercambio', texto: 'Intercambio' },
] as const;

const ORDEN_CLASE: ClaseConvenio[] = ['convenio', 'mli', 'protocolo', 'transporte', 'intercambio', 'multilateral'];

/**
 * Título de la fila de un convenio: la clase (el país ya está en el rótulo del grupo, §11.6). Los multilaterales
 * conservan su título.
 */
export function tituloConvenio(doc: DocNorma, conPais = false): string {
  // Sin país (Favoritos no trae `legislacion`), el título oficial, que lo nombra.
  const pais = paisConvenio(doc);
  if (!pais) return doc.titulo || '';
  const clase = tituloClase(doc);
  return conPais && plegar(pais) !== 'multilateral' ? `${pais} · ${clase}` : clase;
}

function tituloClase(doc: DocNorma): string {
  const clase = claseConvenio(doc);
  if (clase === 'multilateral' || plegar(paisConvenio(doc)) === 'multilateral') return doc.titulo || '';
  if (clase === 'mli') return 'Texto sintetizado con el MLI';
  if (clase === 'protocolo') return 'Protocolo modificatorio del Convenio';
  if (clase === 'intercambio') return 'Acuerdo sobre intercambio de información tributaria';
  if (clase === 'transporte') {
    const t = plegar(doc.titulo || '');
    const modo = /aereo y maritimo/.test(t) ? ' (aéreo y marítimo)' : /maritimo/.test(t) ? ' (marítimo)' : /aereo/.test(t) ? ' (aéreo)' : '';
    return `Convenio sobre transporte internacional${modo}`;
  }
  return 'Convenio para evitar la doble imposición';
}

export interface EstadoConvenio {
  /** «Vigente desde», «Suscrito» o «Publicado». */
  rotulo: string;
  fecha: string | null;
  /** El SII lo informa como suscrito y no vigente: la única señal (ámbar) de la fila. */
  noVigente: boolean;
}

export function estadoConvenio(doc: DocNorma): EstadoConvenio {
  const leg = primero(doc);
  if (/no vigente/i.test(texto(leg.estado_vigencia))) {
    return { rotulo: 'Suscrito', fecha: texto(leg.fecha_suscripcion) || doc.fecha_publicacion || null, noVigente: true };
  }
  if (claseConvenio(doc) === 'mli') return { rotulo: 'Publicado', fecha: doc.fecha_publicacion || null, noVigente: false };
  return { rotulo: 'Vigente desde', fecha: texto(leg.fecha_vigencia) || doc.fecha_publicacion || null, noVigente: false };
}

/** Orden de Convenios por país (alfabético) y, dentro de cada país, por clase y fecha. */
export function ordenarConvenios<T extends DocNorma>(docs: T[]): T[] {
  return docs.slice().sort((a, b) =>
    ordenPais(a).localeCompare(ordenPais(b), 'es')
    || ORDEN_CLASE.indexOf(claseConvenio(a)) - ORDEN_CLASE.indexOf(claseConvenio(b))
    || (a.fecha_publicacion || '').localeCompare(b.fecha_publicacion || ''));
}

// ─── Leyes y normas ─────────────────────────────────────────────────────────────────────────────────────────

/** Siglas que quedan en mayúsculas al pasar a tipo oración. */
const SIGLAS = new Set(['IVA', 'SII', 'DL', 'DFL', 'LIR', 'MLI', 'IDPC', 'RUT', 'UF', 'UTM', 'TTA', 'CT', 'IED', 'I+D', 'D.L.', 'D.F.L.', 'N°', 'Nº']);
/** Nombres propios de cuerpos legales e instituciones que conservan sus mayúsculas. */
const PROPIOS = [
  'Código Tributario', 'Código Civil', 'Código del Trabajo', 'Código de Comercio', 'Servicio de Impuestos Internos',
  'Ley sobre Impuesto a la Renta', 'Impuesto a las Ventas y Servicios', 'Ordenanza de Aduanas', 'Registro Civil',
  'Tribunales Tributarios y Aduaneros', 'Royalty a la Minería', 'Royalty Minero', 'Chile',
];

const casiTodoMayusculas = (t: string) => {
  const letras = t.replace(/[^A-Za-zÁÉÍÓÚÑáéíóúñ]/g, '');
  return letras.length > 0 && letras.replace(/[^A-ZÁÉÍÓÚÑ]/g, '').length / letras.length > 0.8;
};

/**
 * Tipo oración para títulos o materias que vienen en mayúsculas («LEY SOBRE ROYALTY A LA MINERÍA» → «Ley sobre Royalty
 * a la minería»), conservando siglas y nombres propios. Un texto que ya viene en mayúsculas y minúsculas no se toca.
 */
export function tipoOracion(t: string): string {
  const limpio = (t || '').trim();
  if (!casiTodoMayusculas(limpio)) return limpio;
  const esSigla = (p: string) => SIGLAS.has(p) || SIGLAS.has(p.replace(/[,;:]$/, '')) || SIGLAS.has(p.replace(/[,.;:]$/, ''));
  let s = limpio.split(/(\s+)/).map(p => (esSigla(p) ? p : p.toLowerCase())).join('');
  for (const nombre of PROPIOS) s = s.replace(new RegExp(`\\b${plegarPatron(nombre)}\\b`, 'gi'), nombre);
  return s.charAt(0).toUpperCase() + s.slice(1);
}
/** Patrón que calza un nombre con o sin tildes. */
function plegarPatron(nombre: string): string {
  const mapa: Record<string, string> = { a: '[aá]', e: '[eé]', i: '[ií]', o: '[oó]', u: '[uú]', n: '[nñ]' };
  return plegar(nombre).replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/[aeioun]/g, c => mapa[c]);
}

/** Título de la fila de una ley: el oficial en tipo oración, sin el «Núm. 21.850,» que ya va en la columna. */
export function tituloNorma(doc: DocNorma): string {
  const sinNumero = (doc.titulo || '').replace(/^(Ley|Decreto(?: Ley)?|DFL)\s+N[úu]m\.?\s*[\d.]+,?\s*/i, '$1 ');
  return tipoOracion(sinNumero.trim());
}

/** Materias de una ley, sin repetidos y en tipo oración (la frase de una ley sin síntesis, §11.6). */
export function materiasNorma(doc: DocNorma): string[] {
  const vistas = new Set<string>();
  return (doc.materia || '').split(',').map(m => tipoOracion(m.trim())).filter(m => {
    const k = plegar(m);
    if (!k || vistas.has(k)) return false;
    vistas.add(k);
    return true;
  });
}

/** «Ley 21.850», «DL 824», «DFL 30», «Decreto 1027» (columna de datos de Leyes y normas). */
export function etiquetaNorma(doc: DocNorma): string {
  const t = (doc.tipo || '').toLowerCase();
  const n = (doc.numero || '').trim();
  if (t === 'ley') return n ? `Ley ${/^\d{4,}$/.test(n) ? Number(n).toLocaleString('es-CL') : n}` : 'Ley';
  const nombre = t === 'dl' ? 'DL' : t === 'dfl' ? 'DFL' : t === 'decreto' ? 'Decreto' : doc.tipo;
  return n && n !== '0' ? `${nombre} ${n}` : nombre;
}

/** Orden de las pestañas de Leyes y normas (m5). */
export const TIPOS_NORMA = [
  { valor: 'ley', texto: 'Leyes' },
  { valor: 'decreto', texto: 'Decretos' },
  { valor: 'dl', texto: 'Decretos ley' },
  { valor: 'dfl', texto: 'DFL' },
] as const;

/** Artículo del texto: su rótulo para el índice, dónde empieza la línea «Artículo N» y cuánto mide. */
export interface ArticuloTexto { etiqueta: string; inicio: number; largo: number }

const RE_ARTICULO = /^[ \t]*Art[íi]culo\s+(\d+[°º]?(?:\s*(?:bis|ter|qu[aá]ter|quinquies|sexies|septies))?(?:\s+[A-ZÑ](?![a-záéíóúñ]))?|[A-Za-zÁÉÍÓÚáéíóú]+)(\s+transitorio)?(?:\s*\([^)\n]*\))?[ \t]*$/gim;

/** Índice por artículo de una ley (m6): cada línea «Artículo N» del texto, con su posición. */
export function articulosDelTexto(t: string | null | undefined): ArticuloTexto[] {
  const out: ArticuloTexto[] = [];
  if (!t) return out;
  RE_ARTICULO.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = RE_ARTICULO.exec(t)) !== null) {
    const num = m[1].replace(/\s+/g, ' ');
    out.push({ etiqueta: `Art. ${num}${m[2] ? ' trans.' : ''}`, inicio: m.index, largo: m[0].length });
  }
  return out;
}
